import { Download } from "lucide-react";
import { useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { LoaderButton } from "@/components/ui/custom-buttons";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { toSupportedLocale } from "@/i18n";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import {
  triggerBlobDownload,
  useDownloadImportTemplate,
  useImportFields,
} from "@/api/import";
import {
  ImportTable,
  ImportTemplateProfile,
} from "@/lib/import/import-types";

/**
 * Etape 2 : telecharger le modele preformate.
 *
 * Deux profils seulement. Le modele simplifie est le defaut parce que
 * l'exigence est « lisible par un utilisateur novice » : une dizaine de colonnes
 * evidentes plutot que les ~200 du modele complet.
 */
export const ImportStepTemplate = ({
  table,
  onNextStep,
  onBack,
}: {
  table: ImportTable;
  onNextStep: () => void;
  onBack: () => void;
}) => {
  const vt = useVisibleT();
  const { i18n } = useTranslation();
  const { toast } = useToast();
  const [profile, setProfile] = useState<ImportTemplateProfile>("basic");
  const [withExample, setWithExample] = useState(true);

  // Langue d'interface active : les en-têtes du modèle (et plus tard du
  // mapping) doivent suivre ce que l'admin voit, pas `instance.mainLanguage`.
  const lang = toSupportedLocale(i18n.resolvedLanguage ?? i18n.language);
  const { mutateAsync: download, isPending } = useDownloadImportTemplate();
  const { data: fields } = useImportFields(table, lang);

  const basicCount = fields?.filter((field) => field.profile === "basic").length;
  const fullCount = fields?.length;

  const handleDownload = async () => {
    try {
      const response = await download({ table, profile, withExample, lang });
      triggerBlobDownload(
        response,
        `inventozen-import-${table}-${profile}.xlsx`,
      );
    } catch {
      toast({
        variant: "destructive",
        title: vt(
          "bo.import.template.downloadError",
          "Le modèle n'a pas pu être téléchargé.",
        ),
      });
    }
  };

  const cards: Array<{
    value: ImportTemplateProfile;
    title: string;
    description: string;
    count?: number;
    warning?: string;
  }> = [
    {
      value: "basic",
      title: vt("bo.import.template.basic", "Modèle simplifié"),
      description: vt(
        "bo.import.template.basicHint",
        "Les colonnes essentielles, dont les liaisons vers les autres fiches. Recommandé.",
      ),
      count: basicCount,
    },
    {
      value: "full",
      title: vt("bo.import.template.full", "Modèle complet"),
      description: vt(
        "bo.import.template.fullHint",
        "Tous les champs importables.",
      ),
      count: fullCount,
      warning: vt(
        "bo.import.template.fullWarning",
        "Beaucoup de colonnes : utile seulement pour reprendre une base existante.",
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6 max-w-3xl">
      <RadioGroup
        value={profile}
        onValueChange={(value) => setProfile(value as ImportTemplateProfile)}
        className="flex flex-col gap-3"
      >
        {cards.map((card) => (
          <label
            key={card.value}
            className={cn(
              "flex gap-3 p-4 rounded-xl border cursor-pointer",
              profile === card.value
                ? "border-brand-300 ring-4 ring-brand-300/25"
                : "border-gray-200 hover:border-gray-300",
            )}
          >
            <RadioGroupItem value={card.value} className="mt-1" />
            <div className="flex flex-col gap-1">
              <span className="font-medium flex items-baseline gap-2">
                {card.title}
                {card.count != null && (
                  <span className="text-xs text-gray-500 tabular-nums">
                    {card.count} {vt("bo.import.template.columns", "colonnes")}
                  </span>
                )}
              </span>
              <span className="text-sm text-gray-600">{card.description}</span>
              {card.warning && profile === card.value && (
                <span className="text-sm text-amber-700">{card.warning}</span>
              )}
            </div>
          </label>
        ))}
      </RadioGroup>

      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <Checkbox
          checked={withExample}
          onCheckedChange={(value) => setWithExample(value === true)}
        />
        <span>
          {vt(
            "bo.import.template.withExample",
            "Inclure une ligne d'exemple remplie (à supprimer avant l'import)",
          )}
        </span>
      </label>

      <div className="flex flex-wrap items-center gap-3">
        <LoaderButton isLoading={isPending} onClick={handleDownload}>
          <span className="flex items-center gap-2">
            <Download className="w-4 h-4" aria-hidden />
            {vt("bo.import.template.download", "Télécharger le modèle")}
          </span>
        </LoaderButton>
        {/* Un client qui a deja son fichier ne doit pas etre force a telecharger. */}
        <Button variant="ghost" onClick={onNextStep}>
          {vt("bo.import.template.alreadyHaveFile", "J'ai déjà mon fichier")}
        </Button>
      </div>

      <Accordion type="single" collapsible>
        <AccordionItem value="how" className="border rounded-xl px-4">
          <AccordionTrigger className="text-sm">
            {vt("bo.import.template.howTo", "Comment remplir le modèle ?")}
          </AccordionTrigger>
          <AccordionContent>
            <ul className="flex flex-col gap-1.5 text-sm text-gray-700 list-disc pl-4">
              {[
                vt(
                  "bo.import.template.rule.rows",
                  "Une ligne = une fiche. Commencez à saisir à la ligne 4.",
                ),
                vt(
                  "bo.import.template.rule.headers",
                  "Ne modifiez ni la ligne 1 (noms des colonnes) ni la ligne 2 (code technique en gris).",
                ),
                vt(
                  "bo.import.template.rule.example",
                  "Supprimez la ligne d'exemple avant d'importer.",
                ),
                vt(
                  "bo.import.template.rule.relations",
                  "Colonnes de liaison (fond bleu) : copiez l'orthographe exacte depuis l'onglet « Liaisons ». Plusieurs valeurs : séparez-les par un point-virgule.",
                ),
                vt(
                  "bo.import.template.rule.dates",
                  "Dates : 2019-03-05, 05/03/2019, ou seulement l'année 2019.",
                ),
                vt(
                  "bo.import.template.rule.numbers",
                  "Nombres et montants : sans unité ni symbole monétaire.",
                ),
                vt(
                  "bo.import.template.rule.booleans",
                  "Oui / Non : écrivez Oui ou Non.",
                ),
                vt(
                  "bo.import.template.rule.dimensions",
                  "Dimensions : 40 x 60 cm, ou H 40 L 60 P 5 cm, ou Ø 30 cm.",
                ),
                vt(
                  "bo.import.template.rule.noFormula",
                  "Ne fusionnez pas de cellules et collez vos données en « valeurs uniquement ».",
                ),
                vt(
                  "bo.import.template.rule.extraColumns",
                  "Vous pouvez supprimer les colonnes inutiles ou ajouter les vôtres : nous vous dirons ce que nous en avons compris.",
                ),
              ].map((rule) => (
                <li key={rule}>{rule}</li>
              ))}
            </ul>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="flex justify-between">
        <Button variant="ghost" onClick={onBack}>
          {vt("bo.import.back", "Retour")}
        </Button>
        <Button onClick={onNextStep}>
          {vt("bo.import.continue", "Continuer")}
        </Button>
      </div>
    </div>
  );
};
